import JSZip from "jszip";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { renderPages, staticize, waitImages } from "./render";
import { dims } from "./pages";
import { htmlToDocx } from "./docx";

export const safeName = (t) => (t || "Untitled").replace(/[\\/:*?"<>|]+/g, "_").trim().slice(0, 80) || "Untitled";

export function download(blob, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export function toText(html) {
  const el = document.createElement("div");
  el.style.cssText = "position:absolute;left:-30000px;top:0;white-space:pre-wrap;width:800px";
  el.innerHTML = staticize(html);
  el.querySelectorAll(".page-break").forEach((p) => p.replaceWith("\n\f\n"));
  el.querySelectorAll(".sig-field").forEach((s) => {
    const cap = s.querySelector(".sig-caption")?.textContent;
    s.replaceWith(s.querySelector("img") ? `[Signed${cap ? `: ${cap}` : ""}]` : "[Signature: ______________]");
  });
  document.body.appendChild(el);
  const txt = el.innerText;
  el.remove();
  return txt;
}

// Invisible text over the page image so exported PDFs stay searchable and re-importable.
function addTextLayer(pdf, sheet) {
  const base = sheet.getBoundingClientRect();
  const k = 25.4 / 96;
  const w = document.createTreeWalker(sheet, NodeFilter.SHOW_TEXT);
  const r = document.createRange();
  let n;
  while ((n = w.nextNode())) {
    if (n.parentElement.closest(".sheet-watermark")) continue;
    const pt = parseFloat(getComputedStyle(n.parentElement).fontSize) * 0.75;
    for (const m of n.data.matchAll(/\S+/g)) {
      if (/[^\u0000-\u00ff]/.test(m[0])) continue;
      r.setStart(n, m.index);
      r.setEnd(n, m.index + m[0].length);
      const b = r.getBoundingClientRect();
      if (!b.width) continue;
      pdf.setFontSize(pt);
      pdf.text(m[0], (b.left - base.left) * k, (b.bottom - base.top) * k - pt * 0.09, { renderingMode: "invisible" });
    }
  }
}

export async function toPdf(html, s) {
  const d = dims(s);
  const pages = renderPages(html, s);
  const box = document.createElement("div");
  box.style.cssText = "position:fixed;left:-30000px;top:0";
  document.body.appendChild(box);
  const orientation = s.orientation === "landscape" ? "l" : "p";
  const pdf = new jsPDF({ unit: "mm", format: [d.wmm, d.hmm], orientation });
  try {
    for (let i = 0; i < pages.length; i++) {
      box.innerHTML = pages[i];
      await waitImages(box);
      const canvas = await html2canvas(box.firstElementChild, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      if (i) pdf.addPage([d.wmm, d.hmm], orientation);
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, d.wmm, d.hmm);
      addTextLayer(pdf, box.firstElementChild);
    }
  } finally {
    box.remove();
  }
  return pdf.output("blob");
}

export async function buildBlob(fmt, html, s) {
  if (fmt === "txt") return new Blob([toText(html)], { type: "text/plain;charset=utf-8" });
  if (fmt === "pdf") return toPdf(html, s);
  return htmlToDocx(html, s);
}

export async function zipDocs(items, formats) {
  const zip = new JSZip();
  const used = new Set();
  for (const it of items) {
    let base = safeName(it.title);
    let k = 2;
    while (used.has(base)) base = `${safeName(it.title)} (${k++})`;
    used.add(base);
    for (const f of formats) zip.file(`${base}.${f}`, await buildBlob(f, it.html, it.settings));
  }
  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
